import { CONTACT } from "@/lib/contact";
import { CELLARHOST_URL } from "@/lib/external-links";

export const brand = {
    name: "SalesCan",
    tagline: "Founder-led from St. Catharines since 2022",
    email: CONTACT.email,
    phone: CONTACT.phoneDisplay,
    phoneHref: CONTACT.phoneTel,
    address: CONTACT.address,
};

export const nav = [
    { label: "Pricing", href: "/pricing" },
    { label: "Products", href: "/products" },
    { label: "Work", href: "/#work" },
    { label: "About", href: "/#about" },
    { label: "Contact", href: "/#contact" },
];

export const diagnostic = {
    label: "Book the diagnostic",
    short: "Diagnostic",
    hint: "30 minutes. You leave with the leak and a price — whether you hire us or not.",
};

export const hero = {
    kicker: "St. Catharines · Building since 2022",
    meta: "// AI software · SaaS products · Automation",
    title: "We build AI-powered SaaS, websites & automation real businesses run on.",
    subtitle:
        "A founder-led studio in Niagara. We scope the work and stay through delivery — our own products, and websites, chatbots, and workflows for Canadian businesses. Fixed fee. No lock-in.",
    primaryCta: "Start a project",
    secondaryCta: "See products",
    meterCta: "What is one task costing you? Try the meter.",
};

export const marqueeTags = [
    "Founder-led from Niagara",
    "Building since 2022",
    "14 SaaS products",
    "40+ client websites",
    "AI Pipeline Automation",
    "CRM Optimization",
    "Predictive Revenue Analytics",
    "24/7 Lead Qualification",
];

export const roiDefaults = {
    leads: 80,
    dealSize: 5000,
    closeRate: 18,
    lift: 42,
};

export const services = {
    kicker: "What we build for you",
    title: "Three engines. One revenue machine.",
    items: [
        {
            index: "01",
            cat: "Analytics",
            icon: "LineChart",
            title: "Predictive Revenue Analytics",
            desc: "Uncover hidden friction in your pipeline with real-time funnel intelligence, forecast accuracy dashboards and conversion leakage alerts — so every decision is backed by data, not gut feel.",
            image: "https://images.pexels.com/photos/27141314/pexels-photo-27141314.jpeg?auto=compress&cs=tinysrgb&w=900",
        },
        {
            index: "02",
            cat: "Automations",
            icon: "Workflow",
            title: "Hyper-Automated Sales Workflows",
            desc: "Eliminate manual data entry. Auto-enrich leads, sequence follow-ups and trigger instant meeting bookings while your reps focus on closing.",
            image: "https://images.pexels.com/photos/27141307/pexels-photo-27141307.jpeg?auto=compress&cs=tinysrgb&w=900",
        },
        {
            index: "03",
            cat: "AI Agents",
            icon: "Bot",
            title: "Autonomous AI Sales Copilots",
            desc: "Deploy custom AI agents trained on your highest-performing scripts. They qualify leads around the clock, answer objections in real time, and hand off warm prospects to your team — 24/7.",
            image: "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=900",
        },
    ],
};

export const method = {
    kicker: "The SalesCan Method",
    title: "Four chapters to sales velocity.",
    subtitle:
        "No fluff, no 90-slide decks. A disciplined operating system that turns your sales process into a compounding asset.",
    steps: [
        {
            no: "01",
            title: "Friction & Process Audit",
            desc: "We dissect your existing deal flows, CRM bottlenecks and conversion leakages. You get a brutally honest map of where revenue is slipping away.",
        },
        {
            no: "02",
            title: "Architecture & Automation",
            desc: "We build custom multi-channel outreach, CRM syncing and automated follow-up triggers — engineered around how your buyers actually buy.",
        },
        {
            no: "03",
            title: "AI Copilot Integration",
            desc: "We train AI copilots on your best reps' closing frameworks, so every lead gets an instant, on-brand, high-converting response.",
        },
        {
            no: "04",
            title: "Continuous Velocity Scaling",
            desc: "Real-time dashboard tracking, A/B testing of sales scripts and aggressive revenue scaling. Your engine gets faster every single month.",
        },
    ],
};

export const contactCopy = {
    kicker: "Start a project",
    title: "Tell us what you want built.",
    subtitle: "A website, a product, or an automation. Tell us the job and we reply from St. Catharines.",
    goals: ["A website", "A product or SaaS", "Automation", "Not sure yet"],
};

export const salesPage = {
    kicker: "Sales Management",
    title: "Manage, rebuild, and run the pipeline.",
    subtitle:
        "We take a sales process that is leaking, rebuild how deals move, and put AI on the work your team should not do by hand. Then we stay and manage it.",
    offers: [
        {
            no: "01",
            title: "Manage the pipeline",
            body: "Stage hygiene, follow-up, and a weekly forecast so deals do not sit. We run the operating rhythm on the CRM you already have.",
        },
        {
            no: "02",
            title: "Rebuild the process",
            body: "When the motion itself is the leak, we remap stages, handoffs, and sequences around how your buyers actually buy, then leave a system the team can run.",
        },
        {
            no: "03",
            title: "Implement AI",
            body: "First response, qualification, meeting booking, and CRM updates. Copilots trained on how your best reps already sell. People still close.",
        },
    ],
    call: {
        title: "Book a 30-minute diagnostic.",
        body: "We look at the pipeline with you. You leave knowing whether to manage what you have, rebuild it, or add AI — and a price if you want us to do the work. No commitment.",
    },
    faq: [
        {
            q: "Do you replace our sales team?",
            a: "No. We make the team you have more effective. Automation and AI take the busywork. Reps spend their time on conversations that can close.",
        },
        {
            q: "Which CRMs do you work in?",
            a: "HubSpot, Salesforce, Pipedrive, Close, and others. If it has an API, we can wire the pipeline, the follow-up, and the AI into it.",
        },
        {
            q: "How fast does this show up in the pipeline?",
            a: "Most teams see the process change inside the first 60–90 days. The diagnostic includes a timeline tied to the actual bottleneck, not a generic promise.",
        },
        {
            q: "Is the diagnostic free?",
            a: "Yes. Thirty minutes. You walk away with the leak and a price whether you hire us or not.",
        },
    ],
    form: {
        kicker: "Book the call",
        title: "Tell us where the pipeline is stuck.",
        subtitle: "Thirty minutes. You leave with the bottleneck and a price — whether you hire us or not.",
        goals: ["Manage the pipeline", "Rebuild the process", "Implement AI", "Not sure yet"],
    },
};

export const capabilities = {
    kicker: "Our services",
    title: "Sales performance services, end to end.",
    subtitle:
        "Not a scattered list of tactics — one connected revenue system. We plug into how your buyers actually buy and engineer growth at every stage.",
    items: [
        { icon: "Users", title: "Customer Acquisition & Retention", desc: "We do more than fill the top of your funnel. We build buying experiences that turn first-time deals into loyal, expanding accounts." },
        { icon: "Search", title: "Inbound & Search Visibility", desc: "Intent-based keyword research, search-optimized content and authority building so qualified buyers find you across search engines and AI assistants." },
        { icon: "Target", title: "Conversion Rate Optimization", desc: "We map your buyer journey and tighten every step — turning more traffic into booked meetings and more meetings into closed revenue." },
        { icon: "Crosshair", title: "Competitive Intelligence", desc: "We dissect your competitors to expose what's working, where they're weak and where the open lanes are — then build systems to outsell them." },
        { icon: "LineChart", title: "Predictive Revenue Analytics", desc: "Our in-house analysts turn campaign and pipeline data into clear signals, so you double down on what converts and cut what doesn't." },
        { icon: "Sparkles", title: "AI Content & Distribution", desc: "Compelling, on-brand messaging distributed at scale that earns attention and turns it into engagement, leads and pipeline." },
        { icon: "Share2", title: "Social & Community Growth", desc: "We turn social and influencer channels into demand engines that build your brand, grow your community and surface real buying signals." },
        { icon: "Palette", title: "Branding", desc: "A product worth buying deserves a brand worth remembering. We craft a distinctive, authentic identity buyers recognize and trust." },
        { icon: "Code2", title: "Conversion-First Web", desc: "Beautiful isn't enough — your site has to perform. We design and build fast, conversion-focused sites that turn visitors into leads." },
        { icon: "Megaphone", title: "Paid Acquisition", desc: "Don't just buy clicks — buy growth. Scroll-stopping creative and tightly targeted campaigns built to attract the right buyers and drive revenue." },
        { icon: "Mail", title: "Lifecycle & Email", desc: "Your inbox is crowded. We build automated sequences that nurture prospects, revive cold leads and keep customers coming back." },
        { icon: "Smartphone", title: "Mobile & App Growth", desc: "Apps don't grow by accident. Rapid experimentation and store optimization that gets you discovered and converts installs into paying users." },
    ],
};

export const stats = {
    items: [
        { label: "Building since", display: "2022" },
        { label: "SaaS products built", value: 14, prefix: "~", suffix: "", decimals: 0 },
        { label: "Client websites", value: 40, prefix: "", suffix: "+", decimals: 0 },
        { label: "Based in", display: "Canada" },
    ],
};

export const trustedBy = {
    label: "Trusted By",
    logos: [
        "Prime Renovations",
        "Trendz Furniture",
        "Gateway Furniture",
        "AURA Yacht Charters",
        "Prime Auto Glass",
        "Aqua Dental Loft",
        "Anchora Insurance",
        "Northcraft",
        "Neon Oiran",
        "Atelier Forma",
        "Northcrest Homes",
        "Apex Tech",
        "Veloce SaaS",
        "NorthStar Logistics",
        "Maple B2B",
        "Cedar Analytics",
        "Harbour Software",
        "Summit Realty",
        "Vector Labs",
    ],
};

export const team = {
    kicker: "Meet the team",
    intro: "The founder scopes every project and stays through delivery.",
    title: "The people who build it stay on it.",
    subtitle: "A studio in Niagara. We also work with teams outside Canada.",
    cta: "Work With Our Team",
    leaders: [
        {
            name: "William Narrie",
            role: "Chief Executive Officer",
            bio: "William has experience growing businesses from 20+ countries. His strengths lie within growth marketing, lead generation, system building & automation.",
            bw: true,
            img: "https://customer-assets-cm19k8pv.emergentagent.net/job_mobile-redesign-66/artifacts/ioqwwf4s_0a889bcf-7fac-4a3asdasdc-83ec-421086dd6c93.png",
        },
        {
            name: "Jonathan Aufray",
            role: "AI Systems Specialist",
            bio: "Jonathan designs the AI on our products and client work: workflows, lead scoring, and copilots a team can actually run.",
            img: "https://www.growth-hackers.net/wp-content/uploads/2023/03/Jonathan-Aufray-Co-founder-_-CEO-Growth-Hackers-Back_White.jpg",
        },
        {
            name: "Danielle Torno",
            role: "Chief Marketing Officer",
            bio: "Danielle started her career as a customer success manager. She has a strong background in project management, communication & business development.",
            img: "https://www.growth-hackers.net/wp-content/uploads/2022/08/Danielle-Torno-Chief-Operating-Officer-COO-Growth-Hackers.jpg",
        },
        {
            name: "Matthew Davis",
            role: "Growth Marketing Strategist",
            bio: "Matt puts AI into the marketing a smaller company already runs, then measures what actually changes.",
            img: "https://www.growth-hackers.net/wp-content/uploads/2024/04/Matthew-Davis-Growth-Marketing-Strategist-at-GrowthHackers-Growth-Hacking-Agency.jpg",
        },
        {
            name: "Francisco Prado",
            role: "VP of Growth",
            bio: "Francisco works on conversion and the numbers behind it.",
            img: "https://www.growth-hackers.net/wp-content/uploads/2025/09/Francisco-Prado-VP-of-Growth-at-Growth-Hackers-modified.jpeg",
        },
        {
            name: "Alex Morgan",
            role: "Head of Demand Generation",
            bio: "Alex builds demand programs from what the data says is working.",
            img: "https://www.growth-hackers.net/wp-content/uploads/2026/04/Alex-Morgan-Head-of-Demand-Generation-Growth-Hackers.jpg",
        },
        {
            name: "April Murzo",
            role: "Performance Manager",
            bio: "April turns campaign numbers into the next creative decision and stays on the result.",
            img: "https://www.growth-hackers.net/wp-content/uploads/2022/08/April-Murzo-Account-Manager-Growth-Hackers.jpg",
        },
        {
            name: "Alec Ali",
            role: "Creative Strategist",
            bio: "Alec shapes the story a brand tells so the right buyers recognize themselves.",
            img: "https://www.growth-hackers.net/wp-content/uploads/2021/09/Alec-Ali-Chief-Client-Success-Officer-Growth-Hackers.jpg",
        },
        {
            name: "Ron Bernardo",
            role: "Content Marketing Director",
            bio: "Ron has spent over a decade writing the content that brings in traffic and turns it into leads.",
            img: "https://www.growth-hackers.net/wp-content/uploads/2023/09/Ron-Jerome-Bernardo-Content-Marketing-Director-Growth-Hackers-Black_White.jpg",
        },
    ],
    members: [
        {
            name: "Claire Adaza",
            role: "Executive Director",
            img: "https://www.growth-hackers.net/wp-content/uploads/2022/08/Claire-Adaza-Executive-Assistant-Growth-Hackers.jpg",
        },
        {
            name: "John Carl Marlo",
            role: "Multimedia Artist",
            img: "https://www.growth-hackers.net/wp-content/uploads/2024/04/John-Carl-Marlo-Andrade-Graphic-Designer-Video-Editor-GrowthHackers-Growth-Hacking-Company-modified.jpg",
        },
        {
            name: "Aia Ignacio",
            role: "Email Marketing Specialist",
            img: "https://www.growth-hackers.net/wp-content/uploads/2025/07/Aia-Ignacio-Email-Marketing-Specialist-Growth-Hackers.jpg",
        },
        {
            name: "Zap Derrick",
            role: "Business Support Manager",
            img: "https://www.growth-hackers.net/wp-content/uploads/2025/07/Zap-Derrick-Business-Support-Manager-Growth-Hackers.jpg",
        },
    ],
};

export const aiProducts = {
    kicker: "In market",
    title: "AI products we design, build and operate",
    subtitle: "Software we ship and keep running. Open any of them.",
    cta: "Start a project",
    items: [
        {
            name: "RealtorDesk AI",
            status: "beta",
            category: "Real estate",
            description:
                "Lead response for Canadian agents. New enquiries get an answer in English or French, and the follow-up stays in the CRM.",
            line: "Bilingual real estate CRM for Canadian agents",
            detail:
                "RealtorDesk is a bilingual (English and French) CRM for Canadian real estate agents. It scores and triages new leads, keeps every conversation in one thread, and moves deals across a pipeline agents can see, with data hosted in Canada and Realtor.ca listing import.",
            stack: ["React", "Vite", "Supabase", "TypeScript", "Tailwind CSS", "Anthropic Claude", "Google Gemini"],
            href: "https://www.realtordesk.ai/",
            image: "/products/realtordesk.jpg",
            imageAlt: "RealtorDesk – AI CRM for real estate professionals",
            fit: "cover",
        },
        {
            name: "Canada Compliance AI",
            status: "beta",
            category: "Compliance",
            description:
                "CASL, PIPEDA, Quebec Law 25, and AODA for smaller Canadian companies, plus a two-minute assessment.",
            line: "Compliance automation for Canadian businesses",
            detail:
                "Canada Compliance AI shows a Canadian small business where it stands on CASL, PIPEDA and Quebec Law 25 in about two minutes, with AODA guidance, then turns the gaps into a task plan, keeps a breach register and exports the records when someone asks for them. Primary data is hosted in Canada. It supports compliance work; it is not legal advice.",
            stack: ["React", "Vite", "Supabase", "TypeScript", "Tailwind CSS", "OpenAI", "Anthropic Claude"],
            href: "https://www.canadacomplianceai.ca/",
            image: "/products/canadacompliance.jpg",
            imageAlt: "Canada Compliance AI – compliance automation for Canadian businesses",
            fit: "cover",
        },
        {
            name: "CovioIQ",
            status: "live",
            category: "Insurance",
            description:
                "Renewal reminders at 90, 60, and 30 days for Canadian brokers, and a flag when a client looks ready to leave.",
            line: "AI CRM for Canadian insurance brokers",
            detail:
                "CovioIQ is a CRM for Canadian insurance brokerages built around renewals. Every policy enters a renewal pipeline 90 days before expiry and gets a churn-risk score with the reasons named, so brokers know who to call first. The score is an estimate; broker judgment makes the call.",
            href: "https://www.covioiq.com/",
            image: "/products/covioiq.jpg",
            imageAlt: "CovioIQ – AI insurance technology platform for the Canadian market",
            fit: "cover",
        },
        {
            name: "PetVitale",
            status: "live",
            category: "Pet healthcare",
            description: "Records, care reminders, and a way to find a vet.",
            line: "Pet health records and care tools for pet owners",
            detail:
                "PetVitale keeps every pet's health in one place: medical records, vaccinations, medications and care reminders, organized for each pet in the household, plus help finding a vet. It is built for pet parents rather than as clinic-management software, with a free plan for one pet.",
            href: "https://www.petvitale.com/",
            image: "/products/petvitale.jpg",
            imageAlt: "PetVitale – AI pet care and wellness platform dashboard",
            fit: "contain",
        },
        {
            name: "AgriIntel",
            status: "live",
            category: "Agriculture",
            description: "Local weather, spraying windows, and frost alerts for Canadian farms.",
            line: "Hyperlocal weather and alerts for Canadian farms",
            detail:
                "AgriIntel gives Canadian farms weather they can act on: hyperlocal forecasts for each farm's exact coordinates, spray-window guidance, and automated frost, heat and wind alerts for every farm an operation runs. Hosted in Canada, priced in CAD, with no hardware required. Some field-map analytics are still in development.",
            href: "https://www.agriintel.ca/",
            image: "/products/agriintel.jpg",
            imageAlt: "AgriIntel – AI analytics dashboard for agriculture operations",
            fit: "contain",
        },
        {
            name: "HVAC Recovery",
            status: "live",
            category: "HVAC",
            description:
                "Missed-call recovery for independent HVAC shops. After-hours and missed calls are recovered, then proven on a 14-day pilot before it continues month to month.",
            line: "Missed-call recovery for independent HVAC shops",
            detail:
                "After-hours and missed calls are recovered, then proven on a 14-day pilot before it continues month to month.",
            href: "/hvac-recovery/",
            image: "/products/hvac-recovery.jpg",
            imageAlt: "HVAC Recovery – missed-call recovery for independent HVAC shops",
            fit: "cover",
        },
        {
            name: "Navlyt",
            status: "live",
            category: "Aviation",
            description:
                "Charter manuals checked against FAA Part 135, with the gap and the evidence. European and Canadian coverage is planned.",
            line: "Aviation compliance intelligence for charter operators",
            detail:
                "Navlyt reads a charter operator's operations manuals, maps each procedure to FAA 14 CFR Part 135 and quotes the exact clause behind every finding, so the compliance officer checks the evidence instead of taking the software's word for it. AI does the reading; a qualified reviewer accepts or rejects every finding. EASA Part-ORO and Transport Canada CARs coverage is on the roadmap.",
            stack: ["React", "Vite", "Supabase", "TypeScript", "Tailwind CSS", "OpenAI", "Anthropic Claude"],
            href: "https://www.navlyt.com/",
            image: "/products/navlyt.jpg",
            imageAlt: "Navlyt – aviation compliance and operations SaaS platform",
            fit: "contain",
        },
        {
            name: "BuildersBridge",
            status: "live",
            category: "Construction",
            description: "Leads, estimates, crews, invoices, and documents for Canadian builders.",
            line: "AI operating system for Canadian builders and contractors",
            detail:
                "BuildersBridge helps Canadian builders and contractors run every job from lead to payment: leads, estimates, projects, crews, invoices and payments in one place, with Canadian GST, HST and PST built in. Its AI drafts estimates from a job description and the builder approves every line.",
            stack: ["Next.js", "React", "Supabase", "Stripe", "TypeScript", "Tailwind CSS", "Anthropic Claude"],
            href: "https://www.buildersbridge.ca/",
            image: "/products/buildersbridge.jpg",
            imageAlt: "BuildersBridge – AI construction management platform for Canadian builders",
            fit: "cover",
        },
        {
            name: "CellarHost",
            status: "live",
            category: "Wineries",
            description:
                "A digital concierge for wineries and estates. It knows the wines, the property, and the experiences, helps guests choose a bottle or plan a visit, and hands the conversation to someone on the team when it should.",
            line: "A digital concierge for wineries and estates",
            detail:
                "It knows the wines, the property, and the experiences, helps guests choose a bottle or plan a visit, and hands the conversation to someone on the team when it should.",
            href: CELLARHOST_URL,
            image: "/products/cellarhost.jpg",
            imageAlt: "CellarHost – a digital concierge for wineries and estates",
            fit: "cover",
        },
    ],
};

export const work = {
    kicker: "Selected work",
    title: "Websites we've built for real businesses",
    subtitle: "Trades, retail, travel, healthcare, and more. Template concepts are marked on the card.",
    items: [
        {
            id: "prime-renovations",
            name: "Prime Renovations",
            meta: "Construction & trades · Edmonton, AB",
            description: "Quote requests, service pages, and click-to-call for a renovation company in Edmonton.",
            url: "https://www.primerenovations.info",
        },
        {
            id: "trendz-furniture",
            name: "Trendz Furniture",
            meta: "Retail / e-commerce · Edmonton, AB",
            description: "Room collections, a cart, financing, and showroom details for a furniture shop.",
            url: "https://www.trendzfurniture.ca",
        },
        {
            id: "gateway-furniture",
            name: "Gateway Furniture",
            meta: "Retail / furniture · Edmonton, AB",
            description: "Bedroom, sofa, and dining catalogues, with financing, delivery, and reviews.",
            url: "https://www.gatewayfurniture.ca",
        },
        {
            id: "aura-yacht-charters",
            name: "AURA Yacht Charters",
            meta: "Luxury travel · Vancouver, BC",
            description: "Fleet, destinations, and a booking enquiry filtered by place, date, and guest count.",
            url: "https://aura-charters.vercel.app",
        },
        {
            id: "prime-auto-glass",
            name: "Prime Auto Glass",
            meta: "Automotive · BC & Alberta",
            description: "Mobile windshield quotes, insurance billing, and ADAS recalibration across BC and Alberta.",
            url: "https://prime-auto-glass.vercel.app",
        },
        {
            id: "aqua-dental-loft",
            name: "Aqua Dental Loft",
            meta: "Healthcare / dental · Boca Raton, FL",
            description: "Booking, general and cosmetic care, and new-patient info for a Boca Raton practice.",
            url: "https://aqua-dental-loft.vercel.app",
        },
        {
            id: "anchora-insurance",
            name: "Anchora Insurance",
            meta: "Insurance / finance · United States",
            description: "Coverage explainers and a consultation booking for a life-insurance advisory.",
            url: "https://anchora-insurance.vercel.app",
        },
        {
            id: "northcraft",
            name: "Northcraft",
            meta: "Construction & trades · Toronto, ON",
            description: "Custom homes, additions, and galleries, with an estimate and a consultation booking.",
            url: "https://northcraft-theta.vercel.app",
        },
        {
            id: "neon-oiran",
            name: "Neon Oiran",
            meta: "Gaming / entertainment · Worldwide",
            description: "A cinematic game site: trailers, factions, and links to the major stores.",
            url: "https://neon-oiran.vercel.app",
        },
        {
            id: "atelier-forma",
            name: "Atelier Forma",
            meta: "Interior architecture · Global",
            description: "An editorial portfolio: projects, materials, and a journal.",
            url: "https://atelier-forma-site.vercel.app",
        },
        {
            id: "northcrest-homes",
            name: "Northcrest Homes",
            meta: "Custom home building · Calgary, AB",
            description: "A two-minute build-cost estimator, galleries, and the construction process.",
            url: "https://northcrest-home-builders.vercel.app",
        },
        {
            id: "lumiere-aesthetics",
            name: "Lumière Aesthetics",
            meta: "Beauty / aesthetics · Toronto, ON",
            concept: true,
            description: "Botox, laser, and skincare, with galleries, pricing, and a consult booking.",
            url: "https://brainfy-clinic-kit.vercel.app",
        },
        {
            id: "whitfield-cho",
            name: "Whitfield & Cho LLP",
            meta: "Legal services · Toronto, ON",
            concept: true,
            description: "Practice areas, attorney profiles, and a consult booking.",
            url: "https://brainfy-lawfirm-template.vercel.app",
        },
        {
            id: "sterling-vale",
            name: "Sterling & Vale",
            meta: "Real estate · Toronto, ON",
            concept: true,
            description: "GTA search, a home-value estimate, and a handoff into RealtorDesk AI.",
            url: "https://brainfy-real-estate.vercel.app",
        },
    ],
};

export const faq = {
    kicker: "Questions, answered",
    title: "How SalesCan works with you.",
    items: [
        {
            q: "Who is SalesCan?",
            a: "We are a founder-led studio: the founder scopes each project and stays involved through delivery. Based in Niagara, ON. We also work with global businesses — AI consulting and web automation in Niagara.",
        },
        {
            q: "What exactly does SalesCan do?",
            a: "We design and operate our own AI products, and we build websites, chatbots, and workflows for other companies. Pipeline work — manage it, rebuild it, or put AI on the floor — has its own page.",
        },
        {
            q: "How is pricing structured?",
            a: "Fixed packages: Starter $1,997, Growth $3,997, Pro $6,997, and AI Partner from $12,000, plus a monthly care plan. Prices are in CAD. The pricing page has the full comparison. Sales work is quoted from the diagnostic.",
        },
        {
            q: "How long does a website take?",
            a: "Most sites launch in 2–6 weeks, depending on the package. We confirm the timeline before the build starts.",
        },
        {
            q: "Canadian Built",
            a: "Every site is built for Canadian businesses, with PIPEDA, CASL, and AODA alignment from day one. Forms say why they collect information. Marketing email is opt-in, with no pre-checked box. Before launch we check contrast, heading order, and keyboard access. That is a build standard, not a legal opinion.",
        },
    ],
};
